// pages/Dashboard/Finances/InvoicesList.tsx
import React, { useState } from 'react';
import { FileText, Download, Eye, CreditCard, Wallet, TrendingUp, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../../hooks/useLanguage';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {analyticsService} from "../../../services/analyticsService";

interface Invoice {
    _id: string;
    invoiceNumber: string;
    inspectionId: string;
    quoteId: string;
    status: 'draft' | 'finalized' | 'paid' | 'cancelled';
    quoteAmount: number;
    additionalAmount: number;
    totalTTC: number;
    currency: string;
    createdAt: string;
    completedAt?: string;
    paidAt?: string;
}

interface InvoicesListProps {
    garageId: string;
    commissionRate: number;
}

export const InvoicesList: React.FC<InvoicesListProps> = ({ garageId, commissionRate }) => {
    const { t, isRTL, currentLanguage } = useLanguage();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'finalized' | 'paid'>('all');
    const [quoteStatusMap, setQuoteStatusMap] = useState<Map<string, string>>(new Map());

    // Charger les factures
    React.useEffect(() => {
        loadInvoices();
    }, [garageId]);

    const loadInvoices = async () => {
        try {
            setLoading(true);

            // Récupérer les factures
            const invoicesResponse = await analyticsService.getInvoices();
            const allInvoices = invoicesResponse.data || [];

            // Ne garder que finalized et paid
            const filteredInvoices = allInvoices.filter((inv: Invoice) =>
                inv.status === 'finalized' || inv.status === 'paid'
            );

            setInvoices(filteredInvoices);

            // Récupérer les statuts des quotes
            const quoteIds = filteredInvoices.map((inv: Invoice) => inv.quoteId).filter(Boolean);

            if (quoteIds.length > 0) {
                const quotesResponse = await axios.post('/quotes/batch', { quoteIds });
                const quotes = quotesResponse.data || [];

                const statusMap = new Map<string, string>(
                    quotes.map((q: any) => [q._id.toString() as string, q.status as string])
                );
                setQuoteStatusMap(statusMap);
            }

        } catch (error) {
            console.error('Erreur chargement factures:', error);
            toast.error(t('finances.invoices.messages.loadError'));
        } finally {
            setLoading(false);
        }
    };

    // Télécharger une facture
    const handleDownload = async (invoiceId: string, invoiceNumber: string) => {
        try {
            const response = await analyticsService.downloadInvoicesPDF(invoiceId)

            // Créer un lien de téléchargement
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success(t('finances.invoices.messages.downloadSuccess'));
        } catch (error) {
            console.error('Erreur téléchargement:', error);
            toast.error(t('finances.invoices.messages.downloadError'));
        }
    };

    // Voir détails inspection
    const handleViewInspection = (inspectionId: string) => {
        // Rediriger vers la page d'inspection
        window.location.href = `/inspections/${inspectionId}`;
    };

    // Calculer les détails financiers d'une facture
    const getInvoiceFinancials = (invoice: Invoice) => {
        const quoteStatus = quoteStatusMap.get(invoice.quoteId?.toString());
        const isQuoteMobile = quoteStatus === 'accepted_with_payment';

        // Montant quote
        const quoteIsMobile = isQuoteMobile;
        const quoteAmount = invoice.quoteAmount;
        const quoteCommission = quoteAmount * (commissionRate / 100);

        // Montant additional (toujours sur place)
        const additionalAmount = invoice.additionalAmount;
        const additionalCommission = additionalAmount * (commissionRate / 100);

        // Totaux
        const mobileAmount = quoteIsMobile ? quoteAmount : 0;
        const onSiteAmount = (quoteIsMobile ? 0 : quoteAmount) + additionalAmount;
        const totalCommission = quoteCommission + additionalCommission;

        return {
            quoteIsMobile,
            quoteAmount,
            quoteCommission,
            additionalAmount,
            additionalCommission,
            mobileAmount,
            onSiteAmount,
            totalCommission
        };
    };

    // Filtrer les factures
    const filteredInvoices = invoices.filter(inv => {
        if (filter === 'all') return true;
        return inv.status === filter;
    });

    // Obtenir le badge de statut
    const getStatusBadge = (status: string) => {
        const badges: { [key: string]: { color: string; label: string } } = {
            finalized: {
                color: 'bg-green-100 text-green-800',
                label: t('finances.invoices.status.finalized')
            },
            paid: {
                color: 'bg-blue-100 text-blue-800',
                label: t('finances.invoices.status.paid')
            }
        };

        const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', label: status };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.label}
            </span>
        );
    };

    // Formater la date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(
            currentLanguage === 'ar' ? 'ar-SA' :
                currentLanguage === 'en' ? 'en-GB' : 'fr-FR',
            { day: '2-digit', month: 'short', year: 'numeric' }
        );
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="text-indigo-600" size={20} />
                    </div>
                    <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                            {t('finances.invoices.title')}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {t('finances.invoices.subtitle', { count: filteredInvoices.length })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Liste des factures */}
            {filteredInvoices.length === 0 ? (
                <div className="text-center py-8 sm:py-12 text-gray-500">
                    <FileText className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-3" />
                    <p className="text-sm">{t('finances.invoices.noInvoices')}</p>
                </div>
            ) : (
                <div className="space-y-3 sm:space-y-4">
                    {filteredInvoices.map((invoice) => {
                        const financials = getInvoiceFinancials(invoice);

                        return (
                            <div
                                key={invoice._id}
                                className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
                            >
                                {/* Header de la facture */}
                                <div className={`flex items-start sm:items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <div className={`flex items-start sm:items-center gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <div className="bg-indigo-50 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                                    {invoice.invoiceNumber}
                                                </span>
                                                {getStatusBadge(invoice.status)}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {formatDate(invoice.createdAt)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className={`flex gap-1 sm:gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <button
                                            onClick={() => handleDownload(invoice._id, invoice.invoiceNumber)}
                                            className="p-1.5 sm:p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                            title={t('finances.invoices.actions.download')}
                                        >
                                            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* Détails financiers — 1 col mobile, 3 cols md+ */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                                    {/* Montant Mobile */}
                                    {financials.mobileAmount > 0 && (
                                        <div className="bg-blue-50 p-2 sm:p-3 rounded-lg">
                                            <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <CreditCard className="w-4 h-4 text-blue-600" />
                                                <span className="text-xs font-medium text-blue-800">
                                                    {t('finances.invoices.paymentType.mobile')}
                                                </span>
                                            </div>
                                            <div className={`flex items-baseline gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <span className="text-base sm:text-lg font-bold text-blue-900" dir="ltr">
                                                    {financials.mobileAmount.toFixed(2)}
                                                </span>
                                                <span className="text-xs sm:text-sm text-blue-700">{invoice.currency}</span>
                                            </div>
                                            <p className="text-xs text-blue-600 mt-1">
                                                {t('finances.invoices.commission')}: {financials.quoteCommission.toFixed(2)} {invoice.currency}
                                            </p>
                                        </div>
                                    )}

                                    {/* Montant Sur Place */}
                                    {financials.onSiteAmount > 0 && (
                                        <div className="bg-orange-50 p-2 sm:p-3 rounded-lg">
                                            <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <Wallet className="w-4 h-4 text-orange-600" />
                                                <span className="text-xs font-medium text-orange-800">
                                                    {t('finances.invoices.paymentType.onSite')}
                                                </span>
                                            </div>
                                            <div className={`flex items-baseline gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <span className="text-base sm:text-lg font-bold text-orange-900" dir="ltr">
                                                    {financials.onSiteAmount.toFixed(2)}
                                                </span>
                                                <span className="text-xs sm:text-sm text-orange-700">{invoice.currency}</span>
                                            </div>
                                            <p className="text-xs text-orange-600 mt-1">
                                                {t('finances.invoices.commission')}: {(financials.onSiteAmount * (commissionRate / 100)).toFixed(2)} {invoice.currency}
                                            </p>
                                        </div>
                                    )}

                                    {/* Total */}
                                    <div className="bg-green-50 p-2 sm:p-3 rounded-lg">
                                        <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <TrendingUp className="w-4 h-4 text-green-600" />
                                            <span className="text-xs font-medium text-green-800">
                                                {t('finances.invoices.total')}
                                            </span>
                                        </div>
                                        <div className={`flex items-baseline gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                            <span className="text-base sm:text-lg font-bold text-green-900" dir="ltr">
                                                {invoice.totalTTC.toFixed(2)}
                                            </span>
                                            <span className="text-xs sm:text-sm text-green-700">{invoice.currency}</span>
                                        </div>
                                        <p className="text-xs text-green-600 mt-1">
                                            {t('finances.invoices.totalCommission')}: {financials.totalCommission.toFixed(2)} {invoice.currency}
                                        </p>
                                    </div>
                                </div>

                                {/* Travaux supplémentaires si présents */}
                                {invoice.additionalAmount > 0 && (
                                    <div className={`mt-2 sm:mt-3 flex items-center gap-2 text-xs text-orange-600 bg-orange-50 p-2 rounded ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                        <span>
                                            {t('finances.invoices.additionalWork')}: {invoice.additionalAmount.toFixed(2)} {invoice.currency}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};